import { notify } from "@general-backoffice/core";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import getErrorMessage from "utils/getErrorMessage";

import { type ClaimEligibility, type ClaimPreview, type ClaimResult, invoicingEndpoints } from "../../../api";

interface ClaimFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: ClaimResult) => void;
  onRefresh?: () => void;
}

// Flow states
const STATES = {
  LOADING: "loading",
  ELIGIBILITY: "eligibility",
  PREVIEW: "preview",
  CONFIRMING: "confirming",
  SUCCESS: "success",
  ERROR: "error",
} as const;

type FlowState = (typeof STATES)[keyof typeof STATES];

const ClaimFlowModal: React.FC<ClaimFlowModalProps> = ({ isOpen, onClose, onSuccess, onRefresh }) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "claimFlow" });

  // State machine
  const [state, setState] = useState<FlowState>(STATES.LOADING);
  const [eligibility, setEligibility] = useState<ClaimEligibility | null>(null);
  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [result, setResult] = useState<ClaimResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancellingPending, setCancellingPending] = useState(false);
  const [loadingClaimLink, setLoadingClaimLink] = useState(false);

  // Load eligibility when modal opens
  const loadEligibility = useCallback(async () => {
    setState(STATES.LOADING);
    setError(null);

    try {
      // API returns { data, meta } format
      const { data: eligibilityData } = await invoicingEndpoints.checkClaimEligibility();
      setEligibility(eligibilityData);

      if (eligibilityData?.eligible) {
        // Automatically load preview if eligible
        const { data: previewData } = await invoicingEndpoints.getClaimPreview();
        setPreview(previewData);
        setState(STATES.PREVIEW);
      } else {
        setState(STATES.ELIGIBILITY);
      }
    } catch (err: unknown) {
      console.error("Failed to check eligibility:", err);
      const errObj = typeof err === "object" && err !== null ? err : {};
      const errResponse =
        "response" in errObj && typeof errObj.response === "object" && errObj.response !== null ? errObj.response : {};
      const errData =
        "data" in errResponse && typeof errResponse.data === "object" && errResponse.data !== null
          ? errResponse.data
          : {};
      const errError =
        "error" in errData && typeof errData.error === "object" && errData.error !== null ? errData.error : {};
      const errMsg = "message" in errError && typeof errError.message === "string" ? errError.message : null;
      const fallbackMsg = "message" in errObj && typeof errObj.message === "string" ? errObj.message : null;
      setError(errMsg || fallbackMsg || t("loadError"));
      setState(STATES.ERROR);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) {
      loadEligibility();
    } else {
      // Reset state when modal closes
      setState(STATES.LOADING);
      setEligibility(null);
      setPreview(null);
      setResult(null);
      setError(null);
    }
  }, [isOpen, loadEligibility]);

  // Confirm claim
  const handleConfirm = async () => {
    setState(STATES.CONFIRMING);
    setError(null);

    try {
      // API returns { data, meta } format - confirmClaim returns { invoiceId }
      const { data: confirmData } = await invoicingEndpoints.confirmClaim({});
      // Convert the response to ClaimResult format
      const resultData: ClaimResult = {
        success: true,
        invoiceId: confirmData?.invoiceId,
      };

      setResult(resultData);
      setState(STATES.SUCCESS);
      notify.success(t("claimSuccess"));
      onSuccess?.(resultData);
    } catch (err: unknown) {
      console.error("Failed to confirm claim:", err);
      setError(getErrorMessage(err, t("claimError")));
      setState(STATES.ERROR);
    }
  };

  // Open the payout link for an existing pending claim
  const handleOpenClaimLink = async () => {
    const pendingClaim = eligibility?.pendingClaim;
    if (!pendingClaim) return;

    setLoadingClaimLink(true);
    try {
      const { data } = await invoicingEndpoints.getInvoiceClaimLink(pendingClaim.id);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        notify.error(t("claimLinkError"));
      }
    } catch (err: unknown) {
      console.error("Failed to get claim link:", err);
      notify.error(getErrorMessage(err, t("claimLinkError")));
    } finally {
      setLoadingClaimLink(false);
    }
  };

  // Cancel an existing pending claim, then reload eligibility
  const handleCancelPendingClaim = async () => {
    const pendingClaim = eligibility?.pendingClaim;
    if (!pendingClaim) return;

    setCancellingPending(true);
    try {
      await invoicingEndpoints.cancelClaim(pendingClaim.id);
      notify.success(t("pendingClaimCancelled"));
      // Refresh parent table so cancelled claim shows updated status
      onRefresh?.();
      // Reload eligibility — should now be clear
      await loadEligibility();
    } catch (err: unknown) {
      console.error("Failed to cancel pending claim:", err);
      setError(getErrorMessage(err, t("cancelClaimError")));
      setState(STATES.ERROR);
    } finally {
      setCancellingPending(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number | undefined | null): string => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Render eligibility issues
  const renderEligibilityIssues = () => {
    if (!eligibility) return null;

    const { reasons, warnings, taxProfileErrors, pendingClaim } = eligibility;
    const hasPendingClaim = !!pendingClaim;
    // Only filter PENDING_CLAIM_EXISTS when the pending claim card will actually render
    const otherReasons = hasPendingClaim
      ? (reasons?.filter((r) => r !== "PENDING_CLAIM_EXISTS") ?? [])
      : (reasons ?? []);

    return (
      <div>
        {/* Pending claim card */}
        {hasPendingClaim && (
          <Card className="mb-3 border-warning">
            <CardBody>
              <h6 className="mb-3">
                <i className="fas fa-clock text-warning mr-2"></i>
                {t("pendingClaimTitle")}
              </h6>
              <Row className="mb-3">
                <Col>
                  <small className="text-muted">{t("invoiceNumber")}</small>
                  <p className="mb-0 font-weight-bold">{pendingClaim.invoiceNumber || "-"}</p>
                </Col>
                <Col>
                  <small className="text-muted">{t("amount")}</small>
                  <p className="mb-0 font-weight-bold">{formatCurrency(pendingClaim.amount)}</p>
                </Col>
                <Col>
                  <small className="text-muted">{t("date")}</small>
                  <p className="mb-0">
                    {pendingClaim.createdAt ? new Date(pendingClaim.createdAt).toLocaleDateString() : "-"}
                  </p>
                </Col>
              </Row>
              <p className="text-muted small mb-3">{t("pendingClaimDescription")}</p>
              <div className="d-flex gap-2">
                <Button
                  color="primary"
                  size="sm"
                  disabled={loadingClaimLink || cancellingPending}
                  onClick={handleOpenClaimLink}
                >
                  {loadingClaimLink ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <i className="fas fa-external-link-alt mr-1"></i>
                  )}
                  {t("openPayoutLink")}
                </Button>
                <Button
                  color="danger"
                  outline
                  size="sm"
                  disabled={cancellingPending || loadingClaimLink}
                  onClick={handleCancelPendingClaim}
                >
                  {cancellingPending ? <Spinner size="sm" className="mr-1" /> : <i className="fas fa-times mr-1"></i>}
                  {t("cancelPendingClaim")}
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {otherReasons.length > 0 && (
          <Alert color="danger" className="mb-3">
            <h6 className="alert-heading mb-2">
              <i className="fas fa-times-circle mr-2"></i>
              {t("cannotClaim")}
            </h6>
            <ul className="mb-0 pl-3">
              {otherReasons.map((reason, idx) => (
                <li key={idx}>{t(`reasons.${reason}`, reason)}</li>
              ))}
            </ul>
          </Alert>
        )}

        {(taxProfileErrors?.length ?? 0) > 0 && (
          <Alert color="warning" className="mb-3">
            <h6 className="alert-heading mb-2">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {t("taxProfileIssues")}
            </h6>
            <ul className="mb-0 pl-3">
              {taxProfileErrors?.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        {(warnings?.length ?? 0) > 0 && (
          <Alert color="info" className="mb-3">
            <h6 className="alert-heading mb-2">
              <i className="fas fa-info-circle mr-2"></i>
              {t("warnings")}
            </h6>
            <ul className="mb-0 pl-3">
              {warnings?.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        {(eligibility.amount ?? 0) > 0 && (
          <Card className="bg-light">
            <CardBody className="py-3">
              <Row>
                <Col>
                  <small className="text-muted">{t("accumulatedAmount")}</small>
                  <h4 className="mb-0">{formatCurrency(eligibility.amount)}</h4>
                </Col>
                <Col>
                  <small className="text-muted">{t("commissions")}</small>
                  <h4 className="mb-0">{eligibility.commissionCount || 0}</h4>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  // Render invoice preview
  const renderPreview = () => {
    if (!preview?.preview) return null;

    const { preview: inv } = preview;

    return (
      <div>
        {/* Amount Summary */}
        <Card className="mb-4 border-primary">
          <CardBody>
            <Row className="align-items-center">
              <Col>
                <h5 className="text-muted mb-1">{t("netPayable")}</h5>
                <h2 className="mb-0 text-primary">{formatCurrency(inv.netPayable)}</h2>
              </Col>
              <Col xs="auto">
                <Badge color="success" className="px-3 py-2">
                  {t("readyToClaim")}
                </Badge>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Invoice Breakdown */}
        <Card className="mb-4">
          <CardBody>
            <h6 className="text-muted mb-3">
              <i className="fas fa-file-invoice mr-2"></i>
              {t("invoiceBreakdown")}
            </h6>
            <Table size="sm" borderless>
              <tbody>
                <tr>
                  <td>{t("baseAmount")}</td>
                  <td className="text-right">{formatCurrency(inv.baseAmount)}</td>
                </tr>
                {(inv.vatAmount ?? 0) > 0 && (
                  <tr>
                    <td>
                      {t("vat")} ({inv.vatRate}%)
                    </td>
                    <td className="text-right">+ {formatCurrency(inv.vatAmount)}</td>
                  </tr>
                )}
                {(inv.irpfAmount ?? 0) > 0 && (
                  <tr>
                    <td>
                      {t("irpf")} ({inv.irpfRate}%)
                    </td>
                    <td className="text-right text-danger">- {formatCurrency(inv.irpfAmount)}</td>
                  </tr>
                )}
                <tr className="border-top">
                  <td>
                    <strong>{t("netPayable")}</strong>
                  </td>
                  <td className="text-right">
                    <strong>{formatCurrency(inv.netPayable)}</strong>
                  </td>
                </tr>
              </tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Invoice Details */}
        <Card className="mb-4">
          <CardBody>
            <h6 className="text-muted mb-3">
              <i className="fas fa-info-circle mr-2"></i>
              {t("invoiceDetails")}
            </h6>
            <Row>
              <Col md={6}>
                <small className="text-muted">{t("invoiceSeries")}</small>
                <p className="mb-2">
                  <Badge color="secondary">{inv.invoiceSeries}</Badge>
                </p>
              </Col>
              <Col md={6}>
                <small className="text-muted">{t("profileType")}</small>
                <p className="mb-2">{t(`referrerTypes.${inv.referrerType}`, inv.referrerType || "")}</p>
              </Col>
            </Row>
            <small className="text-muted">{t("legalText")}</small>
            <p className="mb-0 small" style={{ whiteSpace: "pre-line" }}>
              {inv.legalText}
            </p>
          </CardBody>
        </Card>

        {/* Parties */}
        <Row>
          <Col md={6}>
            <Card className="h-100">
              <CardBody>
                <h6 className="text-muted mb-2">
                  <i className="fas fa-user mr-2"></i>
                  {t("issuer")}
                </h6>
                <p className="mb-1">
                  <strong>{inv.issuer?.name}</strong>
                </p>
                <p className="mb-0 small text-muted">{inv.issuer?.taxId}</p>
              </CardBody>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100">
              <CardBody>
                <h6 className="text-muted mb-2">
                  <i className="fas fa-building mr-2"></i>
                  {t("recipient")}
                </h6>
                <p className="mb-1">
                  <strong>{inv.recipient?.name}</strong>
                </p>
                <p className="mb-0 small text-muted">{inv.recipient?.taxId}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Render success state
  const renderSuccess = () => {
    if (!result) return null;

    return (
      <div className="text-center py-4">
        <div className="mb-4">
          <i className="fas fa-check-circle text-success" style={{ fontSize: "4rem" }}></i>
        </div>
        <h4 className="mb-3">{t("claimSuccessTitle")}</h4>
        <p className="text-muted mb-4">{t("claimSuccessMessage")}</p>

        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col>
                <small className="text-muted">{t("invoiceNumber")}</small>
                <h5 className="mb-0">{result.invoiceNumber}</h5>
              </Col>
              <Col>
                <small className="text-muted">{t("amount")}</small>
                <h5 className="mb-0">{formatCurrency(result.netPayable)}</h5>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {result.payoutLinkUrl && (
          <Button color="primary" size="lg" href={result.payoutLinkUrl} target="_blank" rel="noopener noreferrer">
            <i className="fas fa-external-link-alt mr-2"></i>
            {t("openPayoutLink")}
          </Button>
        )}
      </div>
    );
  };

  // Get modal title based on state
  const getTitle = () => {
    switch (state) {
      case STATES.LOADING:
        return t("loading");
      case STATES.ELIGIBILITY:
        return t("eligibilityCheck");
      case STATES.PREVIEW:
        return t("claimPreview");
      case STATES.CONFIRMING:
        return t("processing");
      case STATES.SUCCESS:
        return t("claimComplete");
      case STATES.ERROR:
        return t("error");
      default:
        return t("claimCommissions");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        <i className="fas fa-hand-holding-usd mr-2"></i>
        {getTitle()}
      </ModalHeader>

      <ModalBody>
        {state === STATES.LOADING && (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
            <p className="mt-3 text-muted">{t("loadingEligibility")}</p>
          </div>
        )}

        {state === STATES.ELIGIBILITY && renderEligibilityIssues()}

        {state === STATES.PREVIEW && renderPreview()}

        {state === STATES.CONFIRMING && (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
            <p className="mt-3 text-muted">{t("processingClaim")}</p>
            <Progress animated value={100} className="mt-3" />
          </div>
        )}

        {state === STATES.SUCCESS && renderSuccess()}

        {state === STATES.ERROR && (
          <Alert color="danger">
            <h6 className="alert-heading">
              <i className="fas fa-exclamation-circle mr-2"></i>
              {t("errorOccurred")}
            </h6>
            <p className="mb-0">{error}</p>
          </Alert>
        )}
      </ModalBody>

      <ModalFooter>
        {state === STATES.ELIGIBILITY && (
          <>
            <Button color="secondary" onClick={onClose}>
              {t("close")}
            </Button>
            <Button color="primary" onClick={loadEligibility}>
              <i className="fas fa-sync mr-2"></i>
              {t("refresh")}
            </Button>
          </>
        )}

        {state === STATES.PREVIEW && (
          <>
            <Button color="secondary" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button color="success" onClick={handleConfirm}>
              <i className="fas fa-check mr-2"></i>
              {t("confirmClaim")}
            </Button>
          </>
        )}

        {state === STATES.SUCCESS && (
          <Button color="primary" onClick={onClose}>
            {t("done")}
          </Button>
        )}

        {state === STATES.ERROR && (
          <>
            <Button color="secondary" onClick={onClose}>
              {t("close")}
            </Button>
            <Button color="primary" onClick={loadEligibility}>
              <i className="fas fa-redo mr-2"></i>
              {t("tryAgain")}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ClaimFlowModal;
